// src/components/chat/hooks/useDocumentSelection.ts

import { useState } from "react";
import { getSelection, getDocumentWithMetadata } from "../../../helpers/word";
import { createLogger } from "../../../utils/logger";
import { getErrorMessage } from "../../../types/errors";

// Create a logger instance for this hook
const log = createLogger("DocumentSelection");

/**
 * Hook to handle document selection operations for FastAPI integration
 *
 * This simplified version focuses on the core responsibility of extracting
 * document content from Word. The complexity around file saving and
 * configuration settings has been removed since FastAPI handles document
 * context more efficiently through its file-based approach.
 *
 * The hook now has a single, clear responsibility: get document content
 * and return it in a format suitable for the FastAPI model service.
 */
export const useDocumentSelection = () => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Get document context for FastAPI requests
   *
   * This method extracts both the current selection and the full document
   * content. The FastAPI model service will handle converting these strings
   * to File objects and managing caching appropriately.
   *
   * @param options Configuration options for document extraction
   * @returns Document context object or null if error occurred
   */
  const getDocumentContext = async (options?: {
    includeFullDocument?: boolean;
    requireSelection?: boolean;
  }): Promise<{
    currentSelection: string;
    fullDocument?: string;
    trackChangesMode?: string;
    markupMode?: string;
  } | null> => {
    const { includeFullDocument = true, requireSelection = true } = options || {};

    setIsLoading(true);
    setError(null);

    try {
      // TEMPORARY: Selected text feature disabled for this release
      // TODO: Re-enable by reverting this change
      // Original code retrieved selection via: const selectedOoxml = await getSelection();
      const selectedOoxml: string = ""; // Always empty - selection feature disabled

      log.info("Document selection extracted", {
        selectionLength: selectedOoxml.length,
        hasSelection: selectedOoxml.length > 0,
        preview: selectedOoxml.substring(0, 100) + (selectedOoxml.length > 100 ? "..." : ""),
      });

      // Check if selection is required but missing
      if (requireSelection && (!selectedOoxml || selectedOoxml.trim() === "")) {
        setError("Please select some text in the document first.");
        return null;
      }

      // Prepare the base document context
      const documentContext: {
        currentSelection: string;
        fullDocument?: string;
        trackChangesMode?: string;
        markupMode?: string;
      } = {
        currentSelection: selectedOoxml || "", // Will always be empty string now
      };

      // Optionally include the full document
      if (includeFullDocument) {
        try {
          log.debug("Extracting full document content with metadata");
          const { ooxml, trackChangesMode, markupMode } = await getDocumentWithMetadata();

          log.info("Full document extracted with metadata", {
            documentLength: ooxml.length,
            trackChangesMode,
            markupMode,
            selectionRatio:
              selectedOoxml.length > 0
                ? `${Math.round((selectedOoxml.length / ooxml.length) * 100)}%`
                : "0%",
          });

          documentContext.fullDocument = ooxml;
          documentContext.trackChangesMode = trackChangesMode;
          documentContext.markupMode = markupMode;
        } catch (docError: any) {
          log.error("Error extracting full document", { error: docError.message });

          // Decide whether to fail completely or continue with selection only
          if (requireSelection && selectedOoxml.length === 0) {
            // If we have no selection and can't get full document, that's an error
            setError("Could not extract document content. Please try selecting some text.");
            return null;
          }

          // Continue with selection only - log warning but don't fail
          log.warn("Continuing with selection only due to full document extraction failure");
        }
      }

      return documentContext;
    } catch (e: any) {
      log.error("Error in getDocumentContext", { error: e.message, stack: e.stack });

      // Set error message directly from backend or use fallback
      setError(e.message || "Error accessing document content");
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Quick method to check if document selection is available
   *
   * This lightweight method can be used to validate document access
   * without performing the full extraction process.
   */
  const checkDocumentAccess = async (): Promise<boolean> => {
    try {
      setError(null);
      const selection = await getSelection();
      return selection !== null;
    } catch (error) {
      log.error("Document access check failed", { error: getErrorMessage(error) });
      return false;
    }
  };

  /**
   * Reset error state
   *
   * Useful for clearing errors before retry attempts or when switching contexts.
   */
  const resetError = () => {
    setError(null);
  };

  return {
    getDocumentContext,
    checkDocumentAccess,
    isLoading,
    error,
    resetError,
  };
};
